/**
 * StageCompareDialog — V14.88 : compare les étapes d'un lot (maquette blanche →
 * texturée → complète) côte à côte + comparateur « rideau » A/B pour vérifier
 * que la géométrie ne bouge pas d'une étape à l'autre.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Anchor, Layers3 } from 'lucide-react';

const fullImg = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

export const STAGES = [
  { id: 'whitebox', label: '1 · Maquette blanche', short: 'Blanche' },
  { id: 'textured', label: '2 · Texturée', short: 'Texturée' },
  { id: 'furnished', label: '3 · Meublée', short: 'Meublée' },
  { id: 'final', label: '4 · Finale', short: 'Finale' },
  { id: 'full', label: 'Rendu complet', short: 'Complète' },
];

export const stageOf = (item) => item?.params?.render_stage || 'full';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const StageColumn = ({ stage, items, selectedId, onSelect }) => {
  const sel = items.find((i) => i.job_id === selectedId) || null;
  return (
    <div className="flex flex-col gap-1.5 min-w-0" data-testid={`stage-col-${stage.id}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="hud-eyebrow">{stage.label}</span>
        <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>{items.length} rendu{items.length > 1 ? 's' : ''}</span>
      </div>
      <div className="aspect-square rounded-lg overflow-hidden flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid var(--c-border)' }}>
        {sel?.image_url ? (
          <img src={fullImg(sel.image_url)} alt={stage.label} className="w-full h-full object-contain" data-testid={`stage-img-${stage.id}`} />
        ) : (
          <span className="text-[11px] text-center px-3" style={{ color: 'var(--c-text-faint)' }}>Aucun rendu à cette étape</span>
        )}
      </div>
      {items.length > 0 && (
        <Select value={selectedId || ''} onValueChange={onSelect}>
          <SelectTrigger className="h-7 text-[11px] bg-white/[0.04] border-white/10 text-white" data-testid={`stage-select-${stage.id}`}>
            <SelectValue placeholder="Choisir un rendu" />
          </SelectTrigger>
          <SelectContent>
            {items.map((i) => (
              <SelectItem key={i.job_id} value={i.job_id}>
                {(i.is_whitebox_reference || i.is_textured_reference) ? '⚓ ' : ''}{fmtDate(i.completed_at || i.created_at)}{typeof i.fidelity_score === 'number' ? ` · plan ${i.fidelity_score}%` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {sel && (
        <div className="flex items-center gap-1.5 text-[10px] flex-wrap" style={{ color: 'var(--c-text-2)' }}>
          {(sel.is_whitebox_reference || sel.is_textured_reference) && <span className="inline-flex items-center gap-0.5" style={{ color: '#F9A8D4' }}><Anchor className="w-2.5 h-2.5" /> Référence</span>}
          {typeof sel.fidelity_score === 'number' && <span>Fidélité plan {sel.fidelity_score}%</span>}
          {typeof sel.utility_score === 'number' && <span>QA {sel.utility_score}</span>}
        </div>
      )}
    </div>
  );
};

const WipeCompare = ({ a, b, labelA, labelB }) => {
  const [pos, setPos] = useState(50);
  if (!a || !b) {
    return <p className="text-[11px] text-center py-6" style={{ color: 'var(--c-text-faint)' }}>Sélectionnez deux étapes disposant d'un rendu pour activer le comparateur.</p>;
  }
  return (
    <div className="space-y-2" data-testid="stage-wipe">
      <div className="relative w-full rounded-lg overflow-hidden select-none" style={{ aspectRatio: '1 / 1', maxHeight: '48vh', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--c-border)' }}>
        <img src={fullImg(b)} alt={labelB} className="absolute inset-0 w-full h-full object-contain" draggable={false} />
        <img src={fullImg(a)} alt={labelA} className="absolute inset-0 w-full h-full object-contain" draggable={false}
             style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
        <div className="absolute top-0 bottom-0 w-px pointer-events-none" style={{ left: `${pos}%`, background: '#F5C77E', boxShadow: '0 0 0 1px rgba(0,0,0,0.5)' }} />
        <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: '#F5C77E' }}>{labelA}</span>
        <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: 'var(--c-text-2)' }}>{labelB}</span>
      </div>
      <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))}
             className="w-full accent-[#D9A05B]" aria-label="Curseur de comparaison" data-testid="stage-wipe-slider" />
    </div>
  );
};

const StageCompareDialog = ({ open, onClose, items, lots, initialLotId }) => {
  const lotOptions = useMemo(() => {
    const byLot = new Map();
    (items || []).filter((i) => i.status === 'completed' && i.image_url).forEach((i) => {
      if (!byLot.has(i.lot_id)) byLot.set(i.lot_id, new Set());
      byLot.get(i.lot_id).add(stageOf(i));
    });
    return Array.from(byLot.entries()).filter(([, s]) => s.size >= 2).map(([lotId, s]) => ({
      lotId, stages: s.size,
      label: (lots || []).find((l) => l.lot_id === lotId)?.lot_number || lotId,
    }));
  }, [items, lots]);

  const [lotId, setLotId] = useState(initialLotId || lotOptions[0]?.lotId || '');
  useEffect(() => {
    if (!lotOptions.find((o) => o.lotId === lotId)) setLotId(initialLotId && lotOptions.find((o) => o.lotId === initialLotId) ? initialLotId : (lotOptions[0]?.lotId || ''));
  }, [lotOptions, initialLotId, lotId]);

  const perStage = useMemo(() => {
    const done = (items || []).filter((i) => i.lot_id === lotId && i.status === 'completed' && i.image_url);
    const map = {};
    STAGES.forEach((s) => { map[s.id] = done.filter((i) => stageOf(i) === s.id).sort((x, y) => String(y.completed_at || '').localeCompare(String(x.completed_at || ''))); });
    return map;
  }, [items, lotId]);

  const [sel, setSel] = useState({});
  useEffect(() => {
    const next = {};
    STAGES.forEach((s) => {
      const list = perStage[s.id] || [];
      next[s.id] = (list.find((i) => i.is_whitebox_reference || i.is_textured_reference || i.is_furnished_reference) || list[0])?.job_id || '';
    });
    setSel(next);
  }, [perStage]);

  const [pair, setPair] = useState({ a: 'whitebox', b: 'textured' });
  const img = (stageId) => (perStage[stageId] || []).find((i) => i.job_id === sel[stageId])?.image_url;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} data-testid="stage-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Layers3 className="w-4 h-4" style={{ color: 'var(--c-gold)' }} /> Comparateur d'étapes
            <span className="text-[11px] font-normal" style={{ color: 'var(--c-text-faint)' }}>— la géométrie doit rester identique d'une étape à l'autre</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="hud-eyebrow">Lot</span>
          <Select value={lotId} onValueChange={setLotId}>
            <SelectTrigger className="h-8 w-56 text-[12px] bg-white/[0.04] border-white/10 text-white" data-testid="stage-compare-lot">
              <SelectValue placeholder="Choisir un lot" />
            </SelectTrigger>
            <SelectContent>
              {lotOptions.map((o) => <SelectItem key={o.lotId} value={o.lotId}>Lot {o.label} · {o.stages} étapes</SelectItem>)}
            </SelectContent>
          </Select>
          {lotOptions.length === 0 && <span className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>Aucun lot n'a encore au moins deux étapes rendues.</span>}
        </div>

        <div className="grid grid-cols-5 gap-3 mt-3">
          {STAGES.map((s) => (
            <StageColumn key={s.id} stage={s} items={perStage[s.id] || []} selectedId={sel[s.id]} onSelect={(v) => setSel((p) => ({ ...p, [s.id]: v }))} />
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="hud-eyebrow">Rideau A/B</span>
            {['a', 'b'].map((k) => (
              <Select key={k} value={pair[k]} onValueChange={(v) => setPair((p) => ({ ...p, [k]: v }))}>
                <SelectTrigger className="h-7 w-44 text-[11px] bg-white/[0.04] border-white/10 text-white" data-testid={`stage-pair-${k}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{k.toUpperCase()} · {s.short}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            <span style={{ color: 'var(--c-text-faint)' }}>Glissez le curseur : les murs, escaliers et contours doivent se superposer exactement.</span>
          </div>
          <WipeCompare a={img(pair.a)} b={img(pair.b)} labelA={STAGES.find((s) => s.id === pair.a)?.short} labelB={STAGES.find((s) => s.id === pair.b)?.short} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StageCompareDialog;
